import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import { X, ChevronLeft, ChevronRight, Check } from "lucide-react";
import { Button } from "@/shared/ui";
import {
  getPlanConfigSafe,
  type PlanType,
  isSupportedPlan,
} from "@/shared/config";
import { CheckoutSidebar } from "./CheckoutSidebar";
import { AlertTriangle } from "lucide-react";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { analytics } from "@/utils/analytics";

// Import Swiper styles
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserType?: "lawyer" | null;
}

export const PricingModal: React.FC<PricingModalProps> = ({
  isOpen,
  onClose,
  currentUserType,
}) => {
  const { t, i18n } = useTranslation();
  const { subscription } = usePlanLimits();
  const isSpanish = i18n.language === "es";
  const [billingPeriod, setBillingPeriod] = useState<"month" | "year">("month");
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [checkoutSidebarOpen, setCheckoutSidebarOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<PlanType | null>(null);

  // Get current plan from subscription
  const currentPlan = subscription?.planType || null;

  // Group plans by user type - only lawyer plans for now
  const groupedPlans = useMemo(() => {
    const groups: Record<"lawyer", { type: PlanType; isPopular: boolean }[]> = {
      lawyer: [
        { type: "lawyer_professional", isPopular: false },
        { type: "lawyer_professional_pro", isPopular: true },
      ],
    };

    return groups;
  }, []);

  // Initial slide is always 0 since we only have lawyer plans
  const getInitialSlide = () => 0;

  const calculatePrice = (
    basePrice: number,
    period: "month" | "year",
  ): number => {
    if (period === "year") {
      return basePrice * 12 * 0.9; // 10% discount
    }
    return basePrice;
  };

  // Track subscription view when modal opens
  React.useEffect(() => {
    if (isOpen) {
      analytics.subscriptionViewed("pricing_modal");
    }
  }, [isOpen]);

  const handlePlanSelect = (planType: string) => {
    const plan = getPlanConfigSafe(planType);
    if (!plan) return;
    setSelectedPlan(isSupportedPlan(planType) ? planType : null);
    setCheckoutSidebarOpen(true);
  };

  const handleCheckoutSuccess = () => {
    setCheckoutSidebarOpen(false);
    onClose();
    window.location.reload();
  };

  if (!isOpen) return null;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={onClose}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            />

            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 md:p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl max-w-7xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-tiber-200">
                  <div className="flex-1 min-w-0">
                    <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-tiber-900">
                      {t("pricing.modal.title")}
                    </h2>
                  </div>
                  <button
                    onClick={onClose}
                    className="p-2 hover:bg-tiber-200 rounded-lg cursor-pointer transition-colors shrink-0 ml-2"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5 sm:w-6 sm:h-6 text-tiber-600" />
                  </button>
                </div>

                {/* Billing Period Toggle */}
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-tiber-200 bg-romance-50">
                  <div className="flex items-center justify-center gap-2 sm:gap-4 flex-wrap">
                    <span
                      className={`text-xs sm:text-sm font-medium ${
                        billingPeriod === "month"
                          ? "text-tiber-900"
                          : "text-tiber-600"
                      }`}
                    >
                      {t("pricing.modal.monthly")}
                    </span>
                    <button
                      onClick={() =>
                        setBillingPeriod(
                          billingPeriod === "month" ? "year" : "month",
                        )
                      }
                      className={`relative inline-flex h-7 w-12 sm:h-8 sm:w-14 items-center rounded-full transition-colors ${
                        billingPeriod === "year"
                          ? "bg-tiber-500"
                          : "bg-tiber-300"
                      }`}
                      aria-label="Toggle billing period"
                    >
                      <span
                        className={`inline-block h-5 w-5 sm:h-6 sm:w-6 transform rounded-full bg-white transition-transform shadow-sm ${
                          billingPeriod === "year"
                            ? "translate-x-5 sm:translate-x-7"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                    <div className="flex items-center gap-1 sm:gap-2">
                      <span
                        className={`text-xs sm:text-sm font-medium ${
                          billingPeriod === "year"
                            ? "text-tiber-900"
                            : "text-tiber-600"
                        }`}
                      >
                        {t("pricing.modal.yearly")}
                      </span>
                      {billingPeriod === "year" && (
                        <span className="px-2 py-0.5 bg-atlantis-100 text-atlantis-800 text-xs font-semibold rounded-full whitespace-nowrap">
                          {t("pricing.modal.save10")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Swiper Container */}
                <div className="flex-1 overflow-y-auto relative px-3 sm:px-4 md:px-6 lg:px-8 py-4 sm:py-6 min-h-0">
                  <Swiper
                    modules={[Pagination]}
                    pagination={{
                      clickable: true,
                      dynamicBullets: true,
                      bulletClass: "swiper-pagination-bullet",
                      bulletActiveClass: "swiper-pagination-bullet-active",
                    }}
                    spaceBetween={12}
                    slidesPerView={1}
                    initialSlide={getInitialSlide()}
                    breakpoints={{
                      480: {
                        spaceBetween: 16,
                        slidesPerView: 1,
                      },
                      640: {
                        spaceBetween: 20,
                        slidesPerView: 2,
                      },
                      1024: {
                        spaceBetween: 24,
                        slidesPerView: 2,
                      },
                    }}
                    onSwiper={setSwiperInstance}
                    className="w-full! pb-12! sm:pb-14!"
                  >
                    {Object.entries(groupedPlans).map(([userType, plans]) => (
                      <SwiperSlide
                        key={userType}
                        className="flex! w-full flex-col"
                        style={{ width: "100% !important" }}
                      >
                        <div className="h-full w-full flex flex-col gap-3 sm:gap-4">
                          <div className="text-center mb-2 sm:mb-4">
                            <h3 className="text-base sm:text-xl lg:text-2xl font-bold text-tiber-900 capitalize">
                              {t(
                                `pricing.modal.${userType}`,
                                userType.charAt(0).toUpperCase() +
                                  userType.slice(1),
                              )}
                            </h3>
                            <p className="text-xs sm:text-sm text-tiber-600 mt-0.5 sm:mt-1">
                              {t(
                                `pricing.modal.${userType}Description`,
                                `${userType} plans`,
                              )}
                            </p>
                          </div>
                          <div className="w-full p-2 sm:p-4 grid grid-cols-1 sm:grid-cols-2 justify-items-center gap-4 sm:gap-6 md:gap-8 lg:gap-12 flex-1 min-w-0 max-w-4xl mx-auto">
                            {plans.map(({ type, isPopular }) => {
                              const plan = getPlanConfigSafe(type);
                              if (!plan) return null;

                              const Icon = plan.icon ?? Check;
                              const planName = isSpanish
                                ? plan.nameEs
                                : plan.name;
                              const planFeatures = isSpanish
                                ? plan.featuresEs
                                : plan.features;
                              const price = calculatePrice(
                                plan.price,
                                billingPeriod,
                              );
                              const monthlyEquivalent =
                                billingPeriod === "year" ? price / 12 : price;

                              // Check if plan is compatible with current user type
                              const isIncompatible =
                                currentUserType &&
                                plan.userType !== currentUserType;
                              const isCurrentPlan = currentPlan === type;

                              return (
                                <div
                                  key={type}
                                  className="w-full max-w-sm sm:max-w-none mx-auto sm:mx-0"
                                >
                                  <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    className={`w-full min-w-0 relative bg-white rounded-xl shadow-md sm:shadow-lg border-2 transition-all duration-300 hover:shadow-xl flex flex-col h-full ${
                                      isCurrentPlan
                                        ? "border-tiber-600 ring-2 sm:ring-4 ring-tiber-600/20"
                                        : isPopular
                                          ? "border-atlantis-600 hover:border-atlantis-600/50"
                                          : isIncompatible
                                            ? "border-amber-300 opacity-75"
                                            : "border-tiber-600 hover:border-tiber-600/50"
                                    }`}
                                  >
                                    {/* Current Plan Badge */}
                                    {isCurrentPlan && (
                                      <div className="absolute -top-2 sm:-top-3 right-2 sm:right-3 z-10">
                                        <span className="bg-tiber-600 text-white px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-lg whitespace-nowrap">
                                          {t("pricing.currentPlan")}
                                        </span>
                                      </div>
                                    )}
                                    {/* Incompatible Plan Warning */}
                                    {isIncompatible && !isCurrentPlan && (
                                      <div className="absolute top-2 right-2 z-10">
                                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-1.5 sm:p-2 shadow-sm">
                                          <AlertTriangle className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-amber-600" />
                                        </div>
                                      </div>
                                    )}
                                    {/* Popular Badge */}
                                    {isPopular && !isCurrentPlan && (
                                      <div className="absolute -top-2 sm:-top-3 left-1/2 -translate-x-1/2 z-10">
                                        <span className="bg-linear-to-r from-tiber-500 to-tiber-600 text-white px-2.5 sm:px-4 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-semibold shadow-lg whitespace-nowrap">
                                          {t("pricing.mostPopular")}
                                        </span>
                                      </div>
                                    )}

                                    {/* Plan Header */}
                                    <div
                                      className={`bg-linear-to-br ${plan.color} rounded-t-xl p-3 sm:p-4 text-white shrink-0`}
                                    >
                                      <div className="flex items-center justify-center mb-2 sm:mb-3 md:mb-4">
                                        <div className="p-2 sm:p-3 bg-white/20 rounded-lg backdrop-blur-sm">
                                          <Icon className="w-5 h-5 sm:w-7 sm:h-7 md:w-8 md:h-8" />
                                        </div>
                                      </div>
                                      <h4 className="text-base sm:text-lg md:text-xl font-bold text-center mb-1.5 sm:mb-2">
                                        {planName}
                                      </h4>
                                      <div className="text-center mb-1 sm:mb-2">
                                        <span className="text-xl sm:text-2xl md:text-3xl font-bold">
                                          ${price.toFixed(2)}
                                        </span>
                                        <span className="text-white/80 text-[10px] sm:text-xs md:text-sm ml-1 sm:ml-2">
                                          /
                                          {billingPeriod === "month"
                                            ? t("pricing.month")
                                            : t("pricing.year")}
                                        </span>
                                      </div>
                                      {billingPeriod === "year" && (
                                        <p className="text-[10px] sm:text-xs text-center text-white/80">
                                          {t("pricing.modal.equivalentTo")} $
                                          {monthlyEquivalent.toFixed(2)}/
                                          {t("pricing.month")}
                                        </p>
                                      )}
                                    </div>

                                    {/* Features List */}
                                    <div className="p-3 sm:p-4 flex flex-col flex-1 min-h-0">
                                      <ul className="space-y-1.5 sm:space-y-2 mb-3 sm:mb-4 min-h-28 sm:min-h-32 md:min-h-36 max-h-36 sm:max-h-44 md:max-h-52 overflow-y-auto">
                                        {planFeatures.map((feature, index) => (
                                          <li
                                            key={index}
                                            className="flex items-start gap-2"
                                          >
                                            <Check className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-atlantis-600 shrink-0 mt-0.5" />
                                            <span className="text-[11px] sm:text-xs md:text-sm text-tiber-700 leading-snug">
                                              {feature}
                                            </span>
                                          </li>
                                        ))}
                                      </ul>

                                      {/* Incompatible Warning Message */}
                                      {isIncompatible && (
                                        <div className="mb-3 sm:mb-4 p-2 bg-amber-50 border border-amber-200 rounded-lg shrink-0">
                                          <p className="text-[10px] sm:text-xs text-amber-800 text-center">
                                            {t("pricing.incompatiblePlan")}
                                          </p>
                                        </div>
                                      )}

                                      {/* CTA Button */}
                                      <Button
                                        variant={
                                          isPopular ? "primary" : "secondary"
                                        }
                                        size="sm"
                                        className="w-full text-xs sm:text-sm md:text-base mt-auto shrink-0"
                                        onClick={() => handlePlanSelect(type)}
                                        disabled={
                                          isCurrentPlan || !!isIncompatible
                                        }
                                      >
                                        {isCurrentPlan
                                          ? t("pricing.currentPlan")
                                          : isIncompatible
                                            ? t("pricing.incompatiblePlanShort")
                                            : t("pricing.selectPlan")}
                                      </Button>
                                    </div>
                                  </motion.div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </SwiperSlide>
                    ))}
                  </Swiper>

                  {/* Custom Navigation Buttons - only when multiple slides */}
                  {swiperInstance && Object.keys(groupedPlans).length > 1 && (
                    <>
                      <button
                        onClick={() => swiperInstance.slidePrev()}
                        className="absolute left-0 sm:left-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-white/95 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
                        aria-label="Previous"
                        disabled={swiperInstance.isBeginning}
                      >
                        <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-tiber-600" />
                      </button>
                      <button
                        onClick={() => swiperInstance.slideNext()}
                        className="absolute right-0 sm:right-2 top-1/2 -translate-y-1/2 z-10 w-7 h-7 sm:w-9 sm:h-9 md:w-10 md:h-10 bg-white/95 hover:bg-white rounded-full shadow-lg flex items-center justify-center transition-colors disabled:opacity-30 disabled:cursor-not-allowed touch-manipulation"
                        aria-label="Next"
                        disabled={swiperInstance.isEnd}
                      >
                        <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6 text-tiber-600" />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Checkout Sidebar */}
      <CheckoutSidebar
        isOpen={checkoutSidebarOpen}
        onClose={() => setCheckoutSidebarOpen(false)}
        planType={selectedPlan}
        billingPeriod={billingPeriod}
        onSuccess={handleCheckoutSuccess}
      />
    </>
  );
};
